import { useEffect, useState } from "react";
import { DollarSign, Users, Sparkles, TrendingUp, Coins, Award } from "lucide-react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

function Metric({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="p-6 bg-white border border-[#E2E8F0] rounded-md" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">{label}</div>
        <Icon className="w-4 h-4 text-[#14B8A6]" strokeWidth={1.75} />
      </div>
      <div className={`font-display text-3xl font-extrabold ${accent || "text-[#111827]"}`}>{value}</div>
      {sub && <div className="text-xs text-[#64748B] mt-1">{sub}</div>}
    </div>
  );
}

const BAR_COLORS = ["#1F3A8A", "#14B8A6", "#1E40AF", "#0D9488", "#3B82F6"];

export default function Admin() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return <div className="p-12 text-[#64748B]">Loading analytics…</div>;
  }

  const { totals, plan_distribution, top_keywords, top_audiences, top_tools, usage_by_user } = data;

  const keywordData = top_keywords.map(([k, c]) => ({ name: k, count: c }));
  const toolData = top_tools.map(([k, c]) => ({ name: k, count: c }));

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto">
      <div className="mb-8 sm:mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-2">Admin</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Platform analytics.</h1>
        <p className="text-[#64748B] mt-1 text-sm sm:text-base">Revenue, usage and behavior — all in one place.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Metric icon={DollarSign} label="MRR" value={`$${totals.mrr.toLocaleString()}`} sub={`ARR $${totals.arr.toLocaleString()}`} accent="text-[#1F3A8A]" testid="admin-mrr" />
        <Metric icon={Users} label="Active subscribers" value={totals.active_subs} sub={`${totals.users} total users`} testid="admin-active-subs" />
        <Metric icon={Sparkles} label="Prompts generated" value={totals.prompts.toLocaleString()} sub="all-time" testid="admin-prompts" />
        <Metric icon={TrendingUp} label="Revenue" value={`$${totals.revenue.toLocaleString()}`} sub="all-time" accent="text-[#14B8A6]" testid="admin-revenue" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="p-6 bg-white border border-[#E2E8F0] rounded-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Top keywords</h2>
            <Coins className="w-4 h-4 text-[#14B8A6]" />
          </div>
          {keywordData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-[#64748B] text-sm">No data yet</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={keywordData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#64748B", fontSize: 12 }} width={100} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {keywordData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="p-6 bg-white border border-[#E2E8F0] rounded-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Top AI tools</h2>
            <Award className="w-4 h-4 text-[#14B8A6]" />
          </div>
          {toolData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-[#64748B] text-sm">No data yet</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={toolData}>
                  <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {toolData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Plan distribution + Top audiences */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="p-6 bg-white border border-[#E2E8F0] rounded-md">
          <h2 className="font-display text-xl font-bold mb-4">Plan distribution</h2>
          <div className="space-y-3">
            {Object.entries(plan_distribution).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between">
                <div className="text-sm font-semibold uppercase tracking-wider text-[#64748B]">{plan}</div>
                <div className="flex items-center gap-3 flex-1 max-w-xs">
                  <div className="flex-1 h-2 bg-[#F5F7FA] rounded overflow-hidden">
                    <div className="h-full bg-[#1F3A8A]" style={{ width: `${(count / totals.users) * 100}%` }}></div>
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#111827] w-8 text-right">{count}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 bg-white border border-[#E2E8F0] rounded-md">
          <h2 className="font-display text-xl font-bold mb-4">Top audiences (industries)</h2>
          {top_audiences.length === 0 ? (
            <div className="text-[#64748B] text-sm">No data yet</div>
          ) : (
            <div className="space-y-2">
              {top_audiences.slice(0, 8).map(([name, count]) => (
                <div key={name} className="flex items-center justify-between py-2 border-b border-[#E2E8F0] last:border-0">
                  <span className="text-sm text-[#111827] capitalize">{name}</span>
                  <span className="font-mono text-sm text-[#14B8A6] font-semibold">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Usage by user */}
      <div className="p-4 sm:p-6 bg-white border border-[#E2E8F0] rounded-md">
        <h2 className="font-display text-xl font-bold mb-4">Usage by user</h2>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]" data-testid="admin-users-table">
          <thead className="text-xs uppercase tracking-wider text-[#64748B]">
            <tr className="border-b border-[#E2E8F0]">
              <th className="text-left py-2 font-semibold">User</th>
              <th className="text-left py-2 font-semibold">Plan</th>
              <th className="text-right py-2 font-semibold">Used</th>
              <th className="text-right py-2 font-semibold">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {usage_by_user.map((u) => (
              <tr key={u.email} className="border-b border-[#E2E8F0] last:border-0">
                <td className="py-3">
                  <div className="font-semibold text-[#111827]">{u.name || u.email}</div>
                  <div className="text-xs text-[#64748B]">{u.email}</div>
                </td>
                <td className="py-3"><span className="px-2 py-0.5 bg-[#1F3A8A]/10 text-[#1F3A8A] text-xs rounded font-semibold uppercase">{u.plan}</span></td>
                <td className="py-3 text-right font-mono">{u.tokens_used}</td>
                <td className="py-3 text-right font-mono font-semibold text-[#14B8A6]">{u.tokens_remaining}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
